<script setup lang="ts">
import { ref, useTemplateRef, onMounted, computed, onBeforeUnmount, nextTick } from "vue";
import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";

import { BannerData } from "@/SharedBannerApp/types/banner.type";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import CloseButton from "@SharedVueComponents/CloseButton/CloseButton.vue";

import { handleSuccessfulKeycloakProcess } from "@/SharedBannerApp/services/banner-query-listener";

const uid = ref();
const bannerElement = useTemplateRef("bannerElement");
const banner = ref<BannerData>();
const bannerFocusVisible = ref(false);
const bannerOpen = ref(false);
const iconName = computed(() => {
	switch (banner.value?.type) {
		case "alert":
			return "circle-percent";
		case "success":
			return "checkmark-circled";
		case "percent":
			return "circle-percent";
		default:
			return undefined;
	}
});
const bannerLabelId = computed(() => `bannerLabel-${uid.value}`);
const bannerDescriptionId = computed(() => `bannerDescription-${uid.value}`);
const iconClass = computed(() => (banner.value ? `diagonal-${banner.value.type}` : ""));

const _addBanner = async (data: { data: BannerData }) => {
	banner.value = data.data;
	if (!data.data.hideBannerFocus) bannerFocusVisible.value = true;
	await nextTick();
	uid.value = Date.now();
	bannerOpen.value = true;
	bannerElement.value?.show();
	bannerElement.value?.focus();
};

const closeBanner = () => {
	bannerElement.value?.addEventListener("animationend", _removeBanner);
	bannerElement.value?.addEventListener("animationcancel", _removeBanner);
	bannerOpen.value = false;
};

const _removeBanner = () => {
	bannerElement.value?.removeEventListener("animationend", _removeBanner);
	bannerElement.value?.removeEventListener("animationcancel", _removeBanner);
	bannerElement.value?.close();
	banner.value = undefined;
};

onMounted(() => {
	OcmMessenger.subscribe(messengerPublicationTypes.ADD_BANNER, _addBanner);
	OcmMessenger.subscribe(messengerPublicationTypes.ROUTE_CHANGE, closeBanner);
	handleSuccessfulKeycloakProcess();
});

onBeforeUnmount(() => {
	OcmMessenger.unsubscribe(messengerPublicationTypes.ADD_BANNER, _addBanner);
	OcmMessenger.unsubscribe(messengerPublicationTypes.ROUTE_CHANGE, closeBanner);
});
</script>
<!-- --
Hint:
1) shared-banner-container is a container for the banner to be rendered in the header.tag
Its position is placed at the bottom of the header, just 4px above the bottom-border
2) The div around the dialog has additional padding for the dialog-shadow and outline to be visible,
since shared-banner-wrapper has overflow-hidden.
3) shared-banner-wrapper has desktop:-top-4 to counterbalance the additional padding in 2)
-->
<template>
	<Teleport to="#shared-banner-container">
		<div
			v-if="banner"
			class="shared-banner-wrapper tw pointer-events-none relative w-full overflow-hidden desktop:-top-4"
		>
			<div
				class="bottom-unset pointer-events-none fixed bottom-0 m-auto flex w-full max-w-[1408px] justify-center px-2 py-2 desktop:relative desktop:justify-end desktop:py-4"
			>
				<dialog
					ref="bannerElement"
					class="ocm-body1 pointer-events-auto relative m-0 grid min-h-5 max-w-96 grid-cols-[4.8571428571rem,1fr] rounded-lg bg-gray-beton shadow focus-visible:outline-none px360:min-w-86 desktop:min-w-0"
					:class="{
						'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current':
							bannerFocusVisible,
						'shared-banner-animation-enter-active': bannerOpen,
						'shared-banner-animation-leave-active': !bannerOpen,
					}"
					tabindex="-1"
					:aria-label="`${banner?.headline} ${banner?.message}`"
				>
					<CloseButton
						class="right-1 top-1 border-transparent"
						style="
							background-color: transparent !important;
							width: 2.5714286rem !important;
							height: 2.5714286rem !important;
						"
						inverted
						aria-label="Nachricht schließen"
						@click.stop="closeBanner"
					/>
					<div :class="iconClass" class="grid items-center rounded-l-lg px-2.5">
						<SvgIcon class="size-7" :icon-name="iconName" />
					</div>
					<div class="overflow-hidden py-4 pr-11">
						<div :id="bannerLabelId" class="ocm-headline3 mb-1 truncate">{{ banner?.headline }}</div>
						<div :id="bannerDescriptionId" class="truncate">{{ banner?.message }}</div>
					</div>
				</dialog>
			</div>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
.shared-banner-wrapper {
	--enter-duration: 220ms;
	--enter-opacity: 1;
	--enter-translate: 0 100%;
	@media (min-width: 64em) {
		--enter-duration: 320ms;
		--enter-opacity: 0.33;
		--enter-translate: 100%;
	}

	@media (prefers-reduced-motion) {
		--enter-duration: 440ms;
		--enter-opacity: 0;
		--enter-translate: 0;
	}
}

@keyframes shared-banner-animation-slide-in {
	from {
		opacity: var(--enter-opacity);
		translate: var(--enter-translate);
	}
	to {
		opacity: unset;
		translate: unset;
	}
}

@keyframes shared-banner-animation-fade-out {
	from {
		opacity: 1;
	}
	to {
		opacity: 0;
	}
}

.shared-banner-animation-enter-active {
	animation: shared-banner-animation-slide-in var(--enter-duration) ease-out forwards;
}

.shared-banner-animation-leave-active {
	animation: shared-banner-animation-fade-out 360ms ease-in forwards;
}

.diagonal-success {
	background-image: conic-gradient(at 100% 0%, transparent 202deg, var(--color-success-background) 0deg);
}
.diagonal-alert {
	background-image: conic-gradient(at 100% 0%, transparent 202deg, var(--color-error-background) 0deg);
}
.diagonal-percent {
	background-image: conic-gradient(at 100% 0%, transparent 202deg, rgb(221, 220, 2) 0deg);
}

// avoid overlap with secondary flyout
.secondary-flyout-is-open .shared-banner-wrapper {
	visibility: hidden;
}
</style>
